import { useContext, useState } from "react";
import {
  GetVehicleBrochureRequest,
  GetVehicleCardRequest,
  VehicleApiErrorType,
  VehicleCardType,
  getVehicleBrochure,
  getVehicleCard
} from "@coxauto-ui/cxm/communications/api";
import { ConversationContext } from "../../context";

export interface UseVehicleItemParams {
  onInsertContent: (content: string) => void;
}

export interface VehicleInsertResult {
  success: boolean;
  errorType?: VehicleApiErrorType;
}

export function useVehicleItem({ onInsertContent }: UseVehicleItemParams) {
  const [isInserting, setIsInserting] = useState(false);

  const { vinDealerId, env, customerId, customerSystem, autoLeadId } =
    useContext(ConversationContext);

  const insertVehicleCard = async (
    cardType: VehicleCardType
  ): Promise<VehicleInsertResult> => {
    if (!vinDealerId || !autoLeadId) {
      return { success: false };
    }

    setIsInserting(true);
    try {
      const request: GetVehicleCardRequest = {
        dealerId: vinDealerId,
        consumerId: customerId,
        autoLeadId: parseInt(autoLeadId, 10),
        consumerRecordSystem: customerSystem,
        cardType
      };

      const response = await getVehicleCard(request, env);

      if (response?.errorType) {
        return { success: false, errorType: response.errorType };
      }

      if (!response?.body) {
        return { success: false, errorType: "generic" };
      }

      onInsertContent(`<p></p>${response.body}<p></p>`);
      return { success: true };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch vehicle card:", error);
      return { success: false, errorType: "generic" };
    } finally {
      setIsInserting(false);
    }
  };

  const insertVehicleBrochure = async (): Promise<VehicleInsertResult> => {
    if (!vinDealerId || !autoLeadId) {
      return { success: false };
    }

    setIsInserting(true);
    try {
      const request: GetVehicleBrochureRequest = {
        vinDealerId,
        consumerId: customerId,
        consumerRecordSystem: customerSystem,
        autoLeadId: parseInt(autoLeadId, 10)
      };

      const response = await getVehicleBrochure(request, env);

      if (response?.errorType) {
        return { success: false, errorType: response.errorType };
      }

      if (!response?.body) {
        return { success: false, errorType: "generic" };
      }

      onInsertContent(`<p></p>${response.body}<p></p>`);
      return { success: true };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch vehicle brochure:", error);
      return { success: false, errorType: "generic" };
    } finally {
      setIsInserting(false);
    }
  };

  return {
    isInserting,
    insertVehicleCard,
    insertVehicleBrochure
  };
}
